import React from 'react';

// Highlight helper: wraps matched query terms in <b> tags.js.
// Supports multiple comma-separated terms, e.g. "ukraine, drones, putin".
const escapeRegExp = (str = '') => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Tokenize query by handling quotes and various delimiters.
// Examples:
//   'ukraine, drones; putin (kremlin)' -> ["ukraine","drones","putin","kremlin"]
//   '"united states" tech' -> ["united","states","tech"]
//   "AI/ML, C++ & C#" -> ["AI","ML","C++","C#"]
const tokenizeQuery = (q = '') => {
    if (typeof q !== 'string') return [];
    const tokens = [];
    // Match double-quoted, single-quoted phrases, or unquoted runs not containing common delimiters
    const re = new RegExp("\"([^\"]+)\"|'([^']+)'|([^\\s,.;:!?/\\\\\\|(){}<>\"']+)", 'g');
    let m;
    while ((m = re.exec(q)) !== null) {
        const raw = (m[1] || m[2] || m[3] || '').trim();
        if (!raw) continue;
        // If the match came from a quoted group (m[1] or m[2]), split the phrase into separate words
        // so "united states" becomes ["united","states"]. For unquoted runs, keep the token as-is.
        if (m[1] || m[2]) {
            const parts = raw.split(/\s+/).map(s => s.trim()).filter(Boolean);
            tokens.push(...parts);
        } else {
            tokens.push(raw);
        }
    }
    // Dedupe and sort by length desc to prefer longer matches first
    return Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
};

export const highlightText = (text = '', q) => {
    if (!q || typeof q !== 'string') return text;

    // Build terms from query, respecting quotes and splitting on punctuation/whitespace
    const terms = tokenizeQuery(q);
    if (terms.length === 0) return text;

    // Wrap each term with lookarounds to ensure we match full "words" only:
    // not preceded/followed by ASCII word characters (letters, digits, underscore).
    // This handles terms with punctuation like "C++" or "C#".
    const wrapped = terms.map(t => `(?<![A-Za-z0-9_])${escapeRegExp(t)}(?![A-Za-z0-9_])`);
    const pattern = new RegExp(wrapped.join('|'), 'giu');
    if (!pattern.test(text)) return text;
    // Reset lastIndex after test
    pattern.lastIndex = 0;
    const parts = text.split(pattern);
    const matches = [...text.matchAll(pattern)];
    const nodes = [];
    parts.forEach((part, i) => {
        if (part) nodes.push(part);
        if (i < parts.length - 1) {
            const match = matches[i]?.[0] ?? '';
            nodes.push(<b key={`hl-${i}`}>{match}</b>);
        }
    });
    return nodes;
};
